import { api } from "..";

// Every provider the sync groups together sends its pending ids here, not just
// the two it launched with — a league skipped for 30Bet/Superbet/Bwin has to
// name them, otherwise the warning reads as "no provider is waiting on this".
export interface SkippedNoCountryItem {
  nameEn: string | null;
  nameTr: string | null;
  countryCode: string | null;
  countryEn: string | null;
  ugIds: number[];
  gbIds: number[];
  b30Ids: number[];
  sbIds: number[];
  bwIds: number[];
  ugCountryIds: number[];
  gbCountryIds: number[];
  b30CountryIds: number[];
  sbCountryIds: number[];
  bwCountryIds: number[];
}

export interface SyncConflictItem {
  providerId: number;
  providerEntityId: number;
  existingOwnerId: number;
  attemptedOwnerId: number;
}

// Our own league rows disagree about a Sportradar identity (a tid on one
// league, the utid on another) — the group was skipped until a human repairs
// the wrong row.
export interface SportradarIdentityConflictItem {
  utid: number;
  tids: number[];
  leagueIds: number[];
  nameEn: string | null;
  countryEn: string | null;
  countryCode: string | null;
}

export interface WrongSportGroupItem {
  utid: number;
  tids: number[];
  nameEn: string | null;
  sportradarSportId: number;
  expectedSportradarSportId: number;
}

// A group attached to an existing row by exact name (same sport + country)
// instead of creating a duplicate.
export interface LeagueAdoptedItem {
  leagueId: number;
  nameEn: string | null;
  countryEn: string | null;
  utid: number;
}

export interface NameAmbiguousItem {
  nameEn: string | null;
  countryEn: string | null;
  utid: number;
  leagueIds: number[];
}

export type GBUnbridgedReason = "no-country" | "no-match" | "ambiguous";

// A GlobalBahis competition none of whose fixtures carries a Sportradar id.
// Either auto-attached to the one same-name league in its country, or listed
// here with the reason so the admin adds the GB id to the right league.
export interface GBUnbridgedItem {
  competitionId: number;
  name: string;
  categoryName: string | null;
  fixtures: number;
  reason: GBUnbridgedReason;
  candidateLeagueIds: number[];
}

export interface GBUnbridgedMappedItem {
  competitionId: number;
  name: string;
  leagueId: number;
  fixtures: number;
}

export interface SyncProvidersResult {
  totalGroups: number;
  inBoth: number;
  onlyGb: number;
  onlyUg: number;
  mappingsInserted: number;
  leaguesCreated: number;
  leaguesUpdated: number;
  leaguesUpToDate: number;
  sportradarTidsAdded: number;
  sportradarUtidsAdded: number;
  typesBackfilled: number;
  mbsUpdated: number;
  countriesCreated: number;
  countryMappingsInserted: number;
  leagueMappingConflicts: number;
  countryMappingConflicts: number;
  leagueMappingConflictItems: SyncConflictItem[];
  countryMappingConflictItems: SyncConflictItem[];
  skippedNoCountry: number;
  skippedNoName: number;
  skippedNoCountryItems: SkippedNoCountryItem[];
  sportradarIdentityConflictItems: SportradarIdentityConflictItem[];
  wrongSportGroups: number;
  wrongSportGroupItems: WrongSportGroupItem[];
  wrongSportResolutions: number;
  leaguesAdopted: number;
  leaguesAdoptedItems: LeagueAdoptedItem[];
  nameAmbiguousItems: NameAmbiguousItem[];
  gbUnbridgedTotal: number;
  gbUnbridgedMapped: number;
  gbUnbridgedMappedItems: GBUnbridgedMappedItem[];
  gbUnbridgedItems: GBUnbridgedItem[];
  addedByProvider: {
    gb: number;
    ug: number;
    b30: number;
    sb: number;
    bw: number;
  };
}

export const syncProviders = async (): Promise<SyncProvidersResult> => {
  const res = await api.client.post("/leagues-new/sync-providers");
  return res.data;
};

export interface BasketballSyncResult {
  totalLeagues: number;
  leaguesCreated: number;
  leaguesUpToDate: number;
  countryMappingsInserted: number;
  skippedNoCountry: number;
  skippedNoCountryItems: { name: string; country: string | null }[];
  leaguesAdopted: number;
  ambiguousNameItems: { name: string; leagueIds: number[] }[];
}

// Basketball-only league sync (GB). Separate endpoint from football's.
export const syncBasketballProviders =
  async (): Promise<BasketballSyncResult> => {
    const res = await api.client.post(
      "/leagues-new/sync-providers/basketball"
    );
    return res.data;
  };
